<script setup>
import Multiselect from "vue-multiselect";
import Draggable from "vuedraggable";
import {computed, inject, onMounted, ref, watch} from "vue";
import customFieldsService from "@/services/custom-fields.service";
import {trackerTypes} from "@/constants/trackers";
import CustomFieldEdit from "@/components/customFields/edit/CustomFieldEdit.vue";
import trackersService from "@/services/trackers.service";

defineProps(['canEditQuestions']);
const tracker = inject('tracker');
const trackerId = inject('trackerId');
const currentFieldIndex = ref(null);
const addQuestionModal = ref(null);
const questionsList = ref([]);
const showScoutModal = ref(false);

const actualQuestionsList = computed(() => questionsList.value.filter(item => !tracker.questions.some(field => field.field && field.field.id === item.id)));

watch(() => tracker.questions, () => {
    tracker.questions.forEach((item, index) => {
        if (item.field && item.field.id === -1) {
            currentFieldIndex.value = index;
            tracker.questions[index].field = null;
            addQuestionModal.value.open(0);
        }
    })
}, {deep: true});

onMounted(() => {
    getQuestionsList();
});

async function getQuestionsList() {
    const {data} = await customFieldsService.getFieldsArray('tracker_question');
    questionsList.value = data;

    questionsList.value.push({
        id: -1,
        name: "Create New Question",
    });

    if (tracker.type === trackerTypes.PUBLIC && !trackerId.value && !tracker.questions.length) {
        tracker.questions = questionsList.value.filter(item => item.default_question).map(item => ({field: item, required: false}));
    }
}

async function updateQuestion(id) {
    await getQuestionsList();
    tracker.questions[currentFieldIndex.value].field = questionsList.value.find(item => item.id === id);
}

async function addQuestion() {
    let canAddQuestion = true;

    if (tracker.type !== trackerTypes.PUBLIC && trackerId.value) {
        const {data} = await trackersService.hasScout(trackerId.value);

        if (data) {
            showScoutModal.value = true;
            canAddQuestion = false;
        }
    }

    if (canAddQuestion) {
        tracker.questions.push({field: null, required: false})
    }
}
</script>

<template>
    <div>
        <Draggable
            v-model="tracker.questions"
            class="list-drag-n-drop--full list-drag-n-drop--complex"
            :class="{'list-drag-n-drop': canEditQuestions}"
            handle=".draggable"
        >
            <div
                v-for="(question, index) in tracker.questions"
                :key="'q'+index"
                class="w-100"
                :class="{draggable: canEditQuestions}"
            >
                <div class="mb-3 pr-0" :class="{'list-drag-n-drop__item': canEditQuestions}">
                    <span class="p-0"></span>

                    <Multiselect
                        class="multiselect-mid select-questions"
                        v-model="tracker.questions[index].field"
                        :options="actualQuestionsList"
                        :disabled="!canEditQuestions"
                        select-label=""
                        deselect-label=""
                        label="name"
                        track-by="name"
                    >
                        <!-- Customize selected option to add title. Show full question name on mouse over (when question is long) -->
                        <template slot="singleLabel" slot-scope="props"><span class="p-0" :title="props.option.name">{{ props.option.name }}</span></template>
                    </Multiselect>

                    <div v-if="canEditQuestions" class="text-right">
                        <div class="text-right ml-auto">
                            <a @click="tracker.questions.splice(index, 1)" class="link-remove link-remove--round"></a>
                        </div>
                    </div>
                </div>

                <div
                    v-if="canEditQuestions && tracker.questions[index].field && !['static_text','section_text'].includes(tracker.questions[index].field.type)"
                    class="d-flex align-items-center justify-content-between mb-4"
                >
                    <div class="input-checkbox input-checkbox--mandatory">
                        <input :id="'question-mandatory-'+index" type="checkbox" v-model="tracker.questions[index].required">
                        <label :for="'question-mandatory-'+index">Make mandatory</label>
                    </div>
                </div>
            </div>
        </Draggable>

        <div class="link-wrapper" v-if="canEditQuestions">
            <a @click="addQuestion" class="link">+ Add Question</a>
        </div>

        <CustomFieldEdit
            ref="addQuestionModal"
            type="tracker_question"
            @saved="updateQuestion"
        />

        <b-modal v-model="showScoutModal" title="Before You Enable Startup Questions...">
            <p>Startup questions and auto-scout can't be turned on at the same time. Disable auto-scout for this tracker in the
                startups tab by selecting Saved Searches > Manage Saved Searches</p>

            <template #modal-footer>
                <b-button variant="primary" size="lg" @click="showScoutModal = false">Okay</b-button>
            </template>
        </b-modal>
    </div>
</template>

<style scoped>

</style>
